<template>
  <div class="app-layout" :class="currentTemplate">
    <DashboardOnBoarding />
    <!-- Hier tour geadded -->
    <!-- Slideover -->
    <LazyTailwindComponentsSlideOver
      :maxWidth="slideOver?.maxWidth"
      :minWidth="slideOver?.minWidth"
      :title="slideOver?.name"
      :overlayModal="slideOver?.overlayModal || false"
      btn-type="secondary"
      ref="drawer"
      :class="isEditor && 'w-full h-full'"
    >
      <template v-slot:slide-content>
        <component
          :key="slideOverKey"
          :is="slideOver?.content.component"
          v-bind="slideOver?.content.props"
          v-on="slideOver?.content.events || {}"
      /></template>
      <template v-slot:page-content>
        <LayoutHeader :showSecondary="showSecondary" v-if="!isEditor" />
        <main class="app-layout__main" :class="isEditor && 'w-full h-full'">
          <slot />
        </main>
      </template>
    </LazyTailwindComponentsSlideOver>

    <LazyBaseModal
      v-for="(modal, index) in modals"
      :key="index"
      :layoutChange="modal.layoutChange"
      :isFullscreen="modal.isFullscreen"
      :cannotClose="!modal.dismissible"
      :showOverflow="modal.showOverflow"
      :isExtraLarge="modal.isExtraLarge"
      :isLarge="modal.isLarge"
      :isMedium="modal.isMedium"
      :isSmall="modal.isSmall"
      :noPadding="modal.noPadding"
      :maxFit="modal.maxFit"
      :customHeader="modal.customHeader"
      :modalInfo="modal.modalInfo"
      :open="modal.open"
      @close="closeModal"
    />

    <!-- Notification -->
    <transition-group
      tag="div"
      aria-live="assertive"
      class="pointer-events-none fixed inset-0 flex flex-col items-end px-4 py-6 sm:items-start sm:p-6"
      :name="`notification`"
    >
      <div
        class="notification-item"
        v-for="(notification, index) in notifications"
        :key="notification.key"
      >
        <LazyBaseNotification
          :key="notification.key"
          :message="notification.message"
          :details="notification.details"
          :position="notification.position"
          :action="notification.action"
          :actionName="notification.actionName"
          :type="notification.type"
          :duration="notification.duration"
          :indefinite="notification.indefinite"
          :canCloseOnHover="notification.closeable"
          :open="notification.open"
          @close="closeNotification(index)"
        ></LazyBaseNotification>
      </div>
    </transition-group>
  </div>
</template>

<script setup lang="ts">
import SlideOver from '~/components/TailwindComponents/SlideOver.vue'
// Modal
interface Modal {
  isFullscreen?: boolean
  showOverflow?: boolean
  isExtraLarge?: boolean
  isLarge?: boolean
  isMedium?: boolean
  isSmall?: boolean
  noPadding?: boolean
  maxFit?: boolean
  customHeader?: string
  modalInfo?: Object
  open?: boolean
  layoutChange?: boolean
  dismissible?: boolean
}

let slideOver = ref()

const drawer = ref<InstanceType<typeof SlideOver> | null>(null)

const slideOverKey = ref(0)

const route = useRoute()
const { $bus, $features } = useNuxtApp()
const configStore = useConfig()
const settings = ref(configStore.config || {})
const { setLocale } = useI18n()

// Watch for config changes
watch(
  () => configStore.config,
  newConfig => {
    if (newConfig) {
      settings.value = { ...newConfig }
      if (settings.value.locale) {
        setLocale(settings.value.locale)
      }
    }
  },
  { immediate: true }
)

onMounted(() => {
  const config = useRuntimeConfig()
  loadLocale()
  $bus.on('open-modal', createModal)
  $bus.on('close-modal', closeModal)
  $bus.on('modal-close-all', closeAllModals)
  $bus.on('slideOver', setSlideOver)
  $bus.on('notification', createNotification)
  if (window.location.hash) {
    // Remove the fragment (everything after #)
    history.replaceState(null, null, window.location.href.split('#')[0])
  }
})

onUnmounted(() => {
  $bus.off('open-modal', createModal)
  $bus.off('close-modal', closeModal)
  $bus.off('modal-close-all', closeAllModals)
  $bus.off('slideOver', setSlideOver)
  $bus.off('notification', createNotification)
})

const currentTemplate = computed(() => {
  if (route.query?.layout === 'training') return 'app-layout--training'
  if (route.path.includes('training')) return 'app-layout--training'
  if (route.path.includes('models')) return 'app-layout--training'
  if (route.path.includes('data-lab')) return 'app-layout--training'
  if (route.path.includes('datasets')) return 'app-layout--training'
  if (route.path.includes('sites')) return 'app-layout--sites'
  if (isEditor) return 'app-layout--edit'
})

const isEditor = computed(() => {
  return route.path.includes('edit') || route.path.includes('share')
})

const showSecondary = computed(() => {
  return !route.path.includes('datasets')
})

const modals: Ref<Array<Modal>> = ref([])

async function loadLocale() {
  try {
    if (!configStore.config) {
      await configStore.fetchConfig()
    }

    if (configStore.config) {
      settings.value = { ...configStore.config }
      if (settings.value.locale) {
        setLocale(settings.value.locale)
      }
    }
  } catch (error) {
    console.error('Error loading locale:', error)
    settings.value = {} // Ensure settings is never null
  }
}

function setSlideOver(data) {
  if (data.remount) {
    slideOverKey.value++
  }
  slideOver.value = {
    overlayModal: data.overlayModal,
    name: data.slideOver,
    spec: data.spec,
    content: markRaw(data.slideContent),
    props: data.slideContent.props,
    maxWidth: data.maxWidth,
    minWidth: data.minWidth,
  }
  if (data.openImmediately) {
    drawer.value?.open()
  }
}

function createModal(data) {
  if (data?.props?.dismissible !== undefined) {
    data.props.dismissible = data.props.dismissible
  } else {
    data.props = data.props || {}
    data.props.dismissible = true
  }
  const newModal: Modal = {
    dismissible: data.props?.dismissible,
    isFullscreen: data.props?.isFullscreen,
    showOverflow: data.props?.showOverflow,
    isExtraLarge: data.props?.isExtraLarge,
    isLarge: data.props?.isLarge,
    isMedium: data.props?.isMedium,
    isSmall: data.props?.isSmall,
    noPadding: data.props?.noPadding,
    maxFit: data.props?.maxFit,
    customHeader: data.props?.customHeader,
    modalInfo: markRaw(data.modal),
    open: false,
    layoutChange: data.props?.layoutChange,
  }
  modals.value.push(newModal)
  newModal.open = true
}

function closeAllModals() {
  modals.value = []
}

function closeModal() {
  const oldModal = modals.value[modals.value.length - 1]
  if ('open' in oldModal) {
    oldModal.open = false
  }
  setTimeout(() => {
    const index = modals.value.indexOf(oldModal)
    if (index !== -1) {
      modals.value.splice(index, 1)
    }
  }, 200)
}

// Notification
interface Notification {
  key: string
  message: string
  details?: string
  position?: string
  type?: string
  duration?: number
  indefinite?: boolean
  closeable?: boolean
  actionName?: string
  action?: () => void
  open?: boolean
}

const notifications: Ref<Array<Notification>> = ref([])

const createNotification = (data: Notification) => {
  console.log(data)
  let newKey = generateRandomKey()
  const notification: Notification = {
    key: newKey,
    message: data?.message || '',
    details: data?.details || '',
    position: data?.position || 'right',
    type: data?.type || 'info',
    duration: data?.duration || 5000,
    indefinite: data?.indefinite || false,
    closeable: data?.closeable || true,
    actionName: data?.actionName || '',
    action: data?.action || (() => null),
    open: false,
  }
  notifications.value = [notification, ...notifications.value]
}

const closeNotification = (index: number) => {
  notifications.value = notifications.value.filter((n, i) => i !== index)
}

const generateRandomKey = () => {
  const characters = 'ABCDEF'
  let key = ''
  for (let i = 0; i < 10; i++) {
    key += characters.charAt(Math.floor(Math.random() * characters.length))
  }
  return key
}
</script>

<style lang="scss"></style>

<style>
.notification-item {
  @apply flex w-full flex-col items-center space-y-4 pb-2 sm:items-end;
}

.notification-leave-active {
  @apply absolute;
}

.notification-move {
  transition: transform 1s;
}
</style>
